import React, { ReactNode, useCallback, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { BottomSheet as SpringBottomSheet, BottomSheetProps } from "react-spring-bottom-sheet";
import { ReactComponent as ArrowBack } from "../../assets/arrow-back.svg";
import classnames from "classnames/bind";
import styles from "./CustomBottomSheet.module.scss";

const cx = classnames.bind(styles);

interface IBottomSheetProps extends Omit<BottomSheetProps, "open" | "className"> {
  onBack?: () => void;
  defaultOpened?: boolean;
  modalClassName?: string;
  renderPreview?: () => ReactNode;

  children: ReactNode;
  contentClassName?: string;

  textForDefaultPreview?: string;
  descForDefaultPreview?: string;
  iconForDefaultPreview?: ReactNode;
  classNameForDefaultPreview?: string;

  required?: boolean;
  error?: string;

  footerTitle?: string;
  onFooterClick?: () => void;
  open?: boolean;

  showDefaultHeader?: boolean;
  showDefaultPreview?: boolean;
  title?: string;
  previewClassName?: string;
  onPreviewClick?: () => void;
  disabledPreview?: boolean;
}

export interface CustomBottomSheetRef {
  open: () => void;
  close: () => void;
}

export const CustomBottomSheet = forwardRef<CustomBottomSheetRef, IBottomSheetProps>(
  (
    {
      title,
      error,
      onBack,
      required,
      children,
      footerTitle,
      renderPreview,
      onFooterClick,
      onPreviewClick,
      disabledPreview,
      previewClassName,
      contentClassName = "",
      textForDefaultPreview,
      descForDefaultPreview,
      iconForDefaultPreview,
      showDefaultHeader = true,
      showDefaultPreview = true,
      classNameForDefaultPreview,
      snapPoints = ({ maxHeight }) => (window.innerWidth >= 1024 ? [maxHeight * 0.9] : [maxHeight]),
      defaultSnap = ({ snapPoints, lastSnap }) =>  lastSnap ?? Math.min(...snapPoints),
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      open: () => {
        if (typeof props.open === "boolean") return;
        setIsOpen(true);
      },
      close: () => {
        props.onDismiss?.();
        setIsOpen(false);
      },
    }));

    const handleDismiss = useCallback(() => {
      props.onDismiss?.();
      setIsOpen(false);
    }, [props.onDismiss]);

    // useEffect(() => {
    //   if (Capacitor.isNativePlatform()) {
    //     const handleBackButton = (ev: BackButtonEvent) => {
    //       ev.preventDefault();
    //       ev.stopPropagation();

    //       ev.detail.register(101, () => {
    //         handleDismiss();
    //       });
    //     };

    //     document.addEventListener("ionBackButton", handleBackButton as EventListener);

    //     return () => {
    //       document.removeEventListener("ionBackButton", handleBackButton as EventListener);
    //     };
    //   }
    // }, [handleDismiss]);

    const handleOpen = () => {
      if (typeof props.open === "boolean") return;
      setIsOpen(true);
    };

    const handleFooterClick = useCallback(() => {
      onFooterClick?.();
      handleDismiss();
    }, [onFooterClick]);

    const handleBack = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen(false);
        onBack?.();
      },
      [onBack],
    );

    const renderHeader = useMemo(() => {
      if (!showDefaultHeader) return null;
      return (
        <header className="relative flex py-2 items-center w-full">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleBack(e);
            }}
            className="absolute rounded-2xl px-4 py-2 focus-visible:outline-none bg-[#E9E9E9]"
          >
            <ArrowBack className="text-sm font-bold w-3.5 h-3.5" />
          </button>
          {title && <h2 className="w-full">{title}</h2>}
        </header>
      );
    }, [handleBack, title, showDefaultHeader]);

    const renderFooter = useMemo(() => {
      if (!footerTitle) return null;

      return (
        <div className="flex items-center justify-center">
          <button
            className="px-24 py-3 rounded-3xl bg-primary font-bold"
            onClick={(e) => {
              e.stopPropagation();
              handleFooterClick();
            }}
          >
            {footerTitle}
          </button>
        </div>
      );
    }, [handleFooterClick, footerTitle]);

    const renderDefaultPreview = () => {
      if (!showDefaultPreview) return null;

      return (
        <div
          className={cx(
            "flex items-center justify-between px-2.5 py-4 border-t-1 border-b-1 border-gray-200",
            classNameForDefaultPreview,
            {
              "border-red-400": error,
            },
          )}
        >
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <div className="text-[#292929]">{textForDefaultPreview}</div>
              {required && <span className="text-red-500">*</span>}
            </div>
            {descForDefaultPreview && <div className="text-xs text-[#656565]">{descForDefaultPreview}</div>}
          </div>
          {iconForDefaultPreview}
        </div>
      );
    };

    return (
      <>
        <div
          className={previewClassName}
          onClick={(e) => {
            e.stopPropagation();
            if (disabledPreview) return;
            onPreviewClick ? onPreviewClick() : handleOpen();
          }}
        >
          {renderPreview?.() || renderDefaultPreview()}
        </div>
        <SpringBottomSheet
          open={isOpen}
          header={renderHeader}
          footer={renderFooter}
          snapPoints={snapPoints}
          defaultSnap={defaultSnap}
          onDismiss={handleDismiss}
          scrollLocking={false}
          expandOnContentDrag
          {...props}
          className={cx("springBottomSheet", contentClassName)}
        >
          {error && <div className="text-xs text-center text-red-400 mt-4">{error}</div>}
          {children}
        </SpringBottomSheet>
      </>
    );
  },
);
